/**
 * Contact confirmation — modal takeover.
 *
 * Gravity Forms (ajax=true) swaps the form node IN PLACE for the seeded
 * `.c-contact__confirmed` card — so the "you're done" message lands where the
 * form was, with no closure and no focus shift. This module intercepts that swap
 * and presents the confirmation as a LOCKED, centered dialog over a
 * full-viewport frosted-glass backdrop: the deliberate "Connection Confirmed"
 * moment the design calls for.
 *
 * Mechanism (framework-agnostic — does NOT depend on GF's own jQuery event):
 *   1. Cache the pristine form markup at load.
 *   2. A one-shot MutationObserver on the form column fires when GF injects
 *      `.c-contact__confirmed`.
 *   3. Move that card into a built dialog; restore the pristine form BEHIND the
 *      glass (visual continuity — it's inert behind aria-modal + scroll-lock);
 *      open the modal; lock body scroll; trap focus.
 *   4. Exit: "Return to Home" is the seeded native <a href="/"> (fresh page,
 *      top). The X / Esc / backdrop-click reload the page — the simplest
 *      bulletproof form reset (sidesteps GF re-init fragility; the splash
 *      returns pristine and fully functional).
 *
 * JS off → the observer never runs → GF's inline confirmation renders as-is
 * (graceful: still messaged, just not modal'd). NECO's component-JS module
 * pattern (site-header.js).
 */

const column = document.querySelector( '.c-contact__form' );

if ( column ) {
	// Pristine form markup — restored behind the glass so the frosted background
	// shows the form, not an empty slot (GF removes the form on the swap).
	const pristineForm = column.innerHTML;

	// Submit-pending feedback. GF's AJAX round-trip takes a beat; with no cue the
	// button looks dead and users re-click out of impatience (Shannon, 2026-06-10 —
	// the "double-click" was this, not a GF bug). On submit (the event fires only
	// once GF's validation passes and the post begins — a validation failure never
	// fires it) mark the button busy: `.is-submitting` adds a spinner + cursor:wait +
	// pointer-events:none (_button.css), so it visibly works and can't be re-clicked
	// during the wait. Delegated on the column so it survives GF's form re-renders;
	// the modal takeover (or a validation re-render) replaces the form, clearing it.
	column.addEventListener( 'submit', () => {
		const submit = column.querySelector( '[type="submit"]' );
		if ( submit ) {
			submit.classList.add( 'is-submitting' );
			submit.setAttribute( 'aria-busy', 'true' );
		}
	} );

	// Tab-cycle members inside the dialog (close button + the "Return to Home" link).
	const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

	const trapFocus = ( event, dialog ) => {
		const items = dialog.querySelectorAll( FOCUSABLE );
		if ( ! items.length ) {
			return;
		}
		const first = items[ 0 ];
		const last = items[ items.length - 1 ];
		if ( event.shiftKey && document.activeElement === first ) {
			event.preventDefault();
			last.focus();
		} else if ( ! event.shiftKey && document.activeElement === last ) {
			event.preventDefault();
			first.focus();
		}
	};

	const buildModal = ( card ) => {
		const overlay = document.createElement( 'div' );
		overlay.className = 'c-contact-modal';
		overlay.setAttribute( 'role', 'dialog' );
		overlay.setAttribute( 'aria-modal', 'true' );

		const dialog = document.createElement( 'div' );
		dialog.className = 'c-contact-modal__dialog';
		dialog.tabIndex = -1; // programmatic focus target on open (no visible ring)

		// Close affordance — the Figma green disc (node 106:75): a 44px tap target
		// wrapping a 35px translucent power-green disc with a green "X" glyph.
		const close = document.createElement( 'button' );
		close.type = 'button';
		close.className = 'c-contact-modal__close';
		close.setAttribute( 'aria-label', 'Close' );
		close.innerHTML = '<span class="c-contact-modal__x" aria-hidden="true">X</span>';

		// Label the dialog by the confirmation heading.
		const heading = card.querySelector( '.c-contact__confirmed-heading' );
		if ( heading ) {
			heading.id = heading.id || 'neco-confirmed-heading';
			overlay.setAttribute( 'aria-labelledby', heading.id );
		}

		// In the inline swap the card was a live-region focus target; in the dialog
		// the overlay owns the dialog semantics, so strip the card's status role.
		card.removeAttribute( 'role' );
		card.removeAttribute( 'tabindex' );

		dialog.append( close, card );
		overlay.append( dialog );
		return { overlay, dialog, close };
	};

	const open = ( card ) => {
		const { overlay, dialog, close } = buildModal( card );

		// Restore the pristine form behind the glass (inert — focus is trapped in
		// the dialog and body scroll is locked; a dismiss reloads to a live form).
		column.innerHTML = pristineForm;

		document.body.append( overlay );
		document.body.classList.add( 'is-modal-open' );

		// Reveal next frame so the open transition runs from the hidden state — and
		// move focus into the DIALOG (not the close button) once visible: focusing a
		// control would flash its focus ring on open (the green ring Shannon flagged),
		// whereas focusing the dialog container announces it without a ring. Focusing
		// while still `visibility: hidden` is a no-op (focus falls back to <body>),
		// which would leave the dialog un-trapped — hence after the class is added.
		window.requestAnimationFrame( () => {
			overlay.classList.add( 'is-open' );
			dialog.focus();
		} );

		// Every exit closes the modal and reloads → a guaranteed pristine, functional
		// form at the TOP (the hero). The teardown is SYNCHRONOUS and FIRST, so the modal
		// always disappears the instant an exit fires — independent of the reload (if a
		// reload ever lags or is blocked, the prompt still closes; it never appears
		// "stuck"). Then: land at the top by defeating BOTH ways the page can be yanked
		// back to the form on reload —
		//   (1) scroll-memory.js's same-path restore — once submitted, "where you were"
		//       (page-bottom, at the form) is stale. A bare removeItem loses a race (the
		//       body-lock + form-restore shift fire a scroll whose queued rAF save
		//       resurrects the key before unload), so scroll to 0 first — any pending save
		//       then persists 0 — THEN clear the key.
		//   (2) the `#contact` URL fragment the header CTA leaves behind. reload() reloads
		//       the CURRENT url, hash included, so the browser re-applies #contact after
		//       load and jumps straight back down to the form (Shannon, 2026-06-10 — the
		//       "go home conflicts with the previous anchor" jump). Strip the hash via
		//       replaceState (rewrites the url, no navigation/scroll) BEFORE reload, so the
		//       fresh load has no fragment to honor. replaceState (not location='/') keeps
		//       it a real reload → GF re-binds the restored-but-inert form. Top either way.
		const dismiss = () => {
			overlay.remove();
			document.body.classList.remove( 'is-modal-open' );
			window.scrollTo( 0, 0 );
			try {
				window.sessionStorage.removeItem( `neco:scroll:${ window.location.pathname }` );
			} catch {
				/* storage unavailable — scroll-memory is already a no-op there too */
			}
			if ( window.location.hash ) {
				window.history.replaceState( null, '', window.location.pathname + window.location.search );
			}
			window.location.reload();
		};

		close.addEventListener( 'click', dismiss );
		overlay.addEventListener( 'click', ( event ) => {
			if ( event.target === overlay ) {
				dismiss(); // backdrop click (outside the dialog)
			}
		} );

		// Esc + Tab at the document level — Esc must close from anywhere, and the
		// Tab-trap shouldn't depend on the event bubbling up through the overlay (the
		// reload on dismiss tears the listener down, so no explicit removal needed).
		document.addEventListener( 'keydown', ( event ) => {
			if ( event.key === 'Escape' ) {
				dismiss();
			} else if ( event.key === 'Tab' ) {
				trapFocus( event, dialog );
			}
		} );

		// "Return to Home" — route through the same dismiss so it also clears scroll
		// memory and lands at the top (its href="/" is the no-JS fallback).
		const returnLink = card.querySelector( '.c-contact__confirmed-cta' );
		if ( returnLink ) {
			returnLink.addEventListener( 'click', ( event ) => {
				event.preventDefault();
				dismiss();
			} );
		}
	};

	const observer = new MutationObserver( ( mutations, obs ) => {
		for ( const mutation of mutations ) {
			for ( const node of mutation.addedNodes ) {
				if ( node.nodeType !== 1 ) {
					continue;
				}
				const card = node.matches?.( '.c-contact__confirmed' )
					? node
					: node.querySelector?.( '.c-contact__confirmed' );
				if ( card ) {
					obs.disconnect(); // one-shot
					open( card );
					return;
				}
			}
		}
	} );

	observer.observe( column, { childList: true, subtree: true } );
}
